import { Menu } from "antd";
import {
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { Link, To, useNavigate } from "react-router-dom";
import { getDynamicMenu } from "../Helpers/APIEndpoints/Endpoints";
import * as AntIcons from "@ant-design/icons";
import { StarOutlined } from "@ant-design/icons";
import { API } from "../Axios/Axios";
import { usePermission } from "../Helpers/PermissionContext/PermissionContext";

interface DynamicMenuProps {
  permission: number;
  onMenuClick?: () => void;
}

const DynamicMenu: React.FC<DynamicMenuProps> = ({
  permission,
  onMenuClick,
}) => {
  const navigate = useNavigate();
  const { HasPermission } = usePermission();
  const [menuItems, setMenuItems] = useState<any>(null);

  const getIcon = (iconName: string) => {
    const Icon = (AntIcons as any)[iconName];
    if (Icon) {
      return <Icon />;
    }
    // If icon name is not found, return null or a default icon (optional)
    return null;
  };

  interface MenuItemConfig {
    label: string;
    key: string;
    icon: JSX.Element;
    children?:
      | {
          label: any;
          key: any;
          icon: any;
          children: any;
          link: any;
          interfacename: string;
          dashboardname: string;
          requiredPermission: string[];
        }[]
      | null;
    link?: string | null;
    interfacename: string;
    dashboardname: string;
    requiredPermission: string[];
  }

  function getItem({
    label,
    key,
    icon,
    children,
    link,
    interfacename,
    dashboardname,
    requiredPermission,
  }: MenuItemConfig) {
    return {
      label,
      key,
      icon,
      children,
      link,
      interfacename,
      dashboardname,
      requiredPermission,
    };
  }

  useEffect(() => {
    if (permission && permission === 1) {
      // try {
      const getMenuItem = (
        data: any,
        item: {
          permission: any;
          ParentId: any;
          Id: number;
          Name: string;
          SystemName: string;
          Sequence: string;
          Icon: string;
          Link: string | null;
        },
      ) => {
        let iconElement = getIcon(item.Icon);
        let permissionArray = item.permission
          ? item.permission.split(",").map((val: any) => val.trim())
          : [];

        let children =
          data?.filter((x: { ParentId: any }) => x.ParentId === item.Id) ??
          null;

        let childrenMenuItems: any = [];
        if (children) {
          children.forEach(
            (d: {
              ParentId: any;
              Id: number;
              Name: string;
              SystemName: string;
              Sequence: string;
              Icon: string;
              Link: string | null;
            }) => {
              let item = getMenuItem(data, d);
              childrenMenuItems.push(item);
            },
          );
        }

        let menuItem = getItem({
          label: item.Name,
          key: item.Id?.toString(),
          icon: iconElement || <StarOutlined />,
          children: childrenMenuItems,
          link: item.Link,
          interfacename: "",
          dashboardname: "",
          requiredPermission: permissionArray,
        });

        return menuItem;
      };

      const getMenu = async () => {
        const response = await API.post(getDynamicMenu, {});

        // Parse the JSON response
        const data = response.data?.data;

        console.log(data);

        const menuItemsDynamic: {
          label: string;
          key: string;
          icon: JSX.Element;
          children:
            | {
                label: any;
                key: any;
                icon: JSX.Element;
                children: any;
                link: any;
                interfacename: string;
                dashboardname: string;
              }[]
            | null;
          link: string | null;
          interfacename: string;
          dashboardname: string;
          requiredPermission: string[];
        }[] = [];

        data.forEach(
          (d: {
            ParentId: any;
            Id: number;
            Name: string;
            SystemName: string;
            Sequence: string;
            Icon: string;
            Link: string | null;
          }) => {
            if (d.ParentId === null) {
              let item = getMenuItem(data, d);
              menuItemsDynamic.push(item);
            }
          },
        );

        console.log(menuItemsDynamic);
        setMenuItems(menuItemsDynamic);
      };

      getMenu().catch((error) => {
        console.error("Failed to fetch login type:", error);
      });
    }
  }, [permission]);

  const isExternal = (url: string) => /^https?:\/\//i.test(url);

  const handleClick = (e: { key: any }) => {
    if (onMenuClick) {
      onMenuClick(); // sidebar close
    }

    const flatMenu = menuItems.flatMap((i) => [i, ...(i.children ?? [])]);
    const item = flatMenu.find((i) => i.key === e.key);

    const validLinks = flatMenu.map((i) => i.link).filter(Boolean);
    const allPaths = [...validLinks];

    if (item.link === "/InterfaceComponent") {
      navigate(item.link, {
        state: {
          InterfaceName: item?.interfacename,
          DashboardName: item?.dashboardname,
          Mode: "Create",
          // Mode: "Edit",
          RId: 0,
        },
      });
    } else if (item.link === "/DynamicReport") {
      navigate(item.link, {
        state: {
          InterfaceName: item?.interfacename,
          DashboardName: item?.dashboardname,
        },
      });
    }
    else if (isExternal(item.link)) {
        window.open(item.link, "_blank", "noopener,noreferrer");
    }
    else if (allPaths.includes(item.link)) {
      navigate(item.link);
    }
  };

  const filterMenu = (menuItems: any[]) =>
    menuItems
      ?.map((item) => {
        if (item.children.length > 0) {
          const filteredChildren = filterMenu(item.children);
          if (filteredChildren?.length === 0) return null;
          return { ...item, children: filteredChildren };
        }

        if (item.requiredPermission?.length === 0) {
          return item;
        }

        if (
          !item.requiredPermission ||
          HasPermission(item.requiredPermission)
        ) {
          return item;
        }
        return null;
      })
      .filter(Boolean);

  return (
    <Menu
      theme="dark"
      mode="inline"
      className="menu_items"
      defaultSelectedKeys={["1"]}
      onClick={handleClick}
    >
      {filterMenu(menuItems)?.map(
        (item: {
          children: any[];
          key: Key | null | undefined;
          icon:
            | string
            | number
            | boolean
            | ReactElement<any, string | JSXElementConstructor<any>>
            | Iterable<ReactNode>
            | null
            | undefined;
          label:
            | string
            | number
            | boolean
            | ReactElement<any, string | JSXElementConstructor<any>>
            | Iterable<ReactNode>
            | null
            | undefined;
          path: To;
        }) =>
          item.children?.length > 0 ? (
            <Menu.SubMenu key={item.key} icon={item.icon} title={item.label}>
              {item.children?.map((child) => (
                <Menu.Item key={child.key} icon={child.icon}>
                  <Link style={{ textDecoration: "none" }} to={child.path}>
                    {child.label}
                  </Link>
                </Menu.Item>
              ))}
            </Menu.SubMenu>
          ) : (
            <Menu.Item key={item.key} icon={item.icon}>
              <Link style={{ textDecoration: "none" }} to={item.path}>
                {item.label}
              </Link>
            </Menu.Item>
          ),
      )}
    </Menu>
  );
};

export default DynamicMenu;
